import posthog from 'posthog-js';
import React, { UIEvent, useEffect, useRef, useState } from 'react';
import { API_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';
import ParlayTicketSlip from './ParlayTicketSlip';
import './Parlays.css';

interface ParlayLeg {
  ingest_market_id: string;
  outcome_label: string;
  snap_implied_prob: number | null;
  ends_at: string | null;
  status: string;
  market_question: string;
  market_icon: string | null;
  event_title: string;
  event_image: string | null;
  category: string | null;
  sub_category: string | null;
}

interface Parlay {
  parlay_id: string;
  total_amount: number;
  total_odds: number;
  expected_payout: number;
  status: string;
  settlement_date: string;
  created_at: string;
  actual_cashout_amount?: number;
  legs: ParlayLeg[];
}

interface ParlaysProps {
  onBack: () => void;
}

const Parlays: React.FC<ParlaysProps> = ({ onBack }) => {
  const { user } = useAuth();
  const [parlays, setParlays] = useState<Parlay[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedFilter, setSelectedFilter] = useState('Active');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  // No dropdown; simple buttons for filters

  useEffect(() => {
    const fetchParlays = async () => {
      if (!user?.issuer) {
        setError('User not authenticated');
        setLoading(false);
        return;
      }

      try {
        const statusParam = selectedFilter ? `&status=${selectedFilter.toLowerCase()}` : '';
        const response = await fetch(`${API_URL}/user/parlays/active?user_issuer=${user.issuer}${statusParam}`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setParlays(data.parlays || []);
      } catch (err) {
        console.error('Error fetching parlays:', err);
        setError('Failed to load parlays');
      } finally {
        setLoading(false);
      }
    };

    fetchParlays();
  }, [user?.issuer, selectedFilter]);


  if (loading) {
    return (
      <div className="parlays-container">
        <div className="parlays-header">
          <button onClick={onBack} className="back-button">← Back</button>
          <h2>Parlays</h2>
        </div>
        <div className="loading-state">Loading your parlays...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="parlays-container">
        <div className="parlays-header">
          <button onClick={onBack} className="back-button">← Back</button>
          <h2>Parlays</h2>
        </div>
        <div className="error-state">{error}</div>
      </div>
    );
  }

  return (
    <div className="parlays-container">
      <div className="parlays-header">
        <button onClick={onBack} className="back-button">← Back</button>
        <div className="parlay-filter-toggle" role="tablist" aria-label="Parlay filter">
          <button
            className={`parlay-filter-btn ${selectedFilter === 'Active' ? 'active' : ''}`}
            onClick={() => { const next = selectedFilter === 'Active' ? '' : 'Active'; setSelectedFilter(next); try { posthog.capture('engagement:parlays_tab', { tab: 'Active', selected: next === 'Active' }); } catch {} }}
            aria-pressed={selectedFilter === 'Active'}
          >
            Active
          </button>
          <button
            className={`parlay-filter-btn ${selectedFilter === 'Settled' ? 'active' : ''}`}
            onClick={() => { const next = selectedFilter === 'Settled' ? '' : 'Settled'; setSelectedFilter(next); try { posthog.capture('engagement:parlays_tab', { tab: 'Settled', selected: next === 'Settled' }); } catch {} }}
            aria-pressed={selectedFilter === 'Settled'}
          >
            Settled
          </button>
        </div>
      </div>
      
      {parlays.length === 0 ? (
        <div className={`empty-state ${selectedFilter === 'Settled' ? 'no-border' : ''}`}>
          <p>None of your parlays have settled yet</p>
        </div>
      ) : (
        <>
          <div
            className={`parlays-slips-container ${
              parlays.length === 1 ? 'single-parlay' :
              parlays.length === 2 ? 'double-parlay' :
              'multiple-parlays'
            }`}
            ref={containerRef}
            onScroll={(e: UIEvent<HTMLDivElement>) => {
              const el = e.currentTarget;
              const width = el.clientWidth || 1;
              const index = Math.round(el.scrollLeft / width);
              if (index !== currentIndex) setCurrentIndex(Math.min(Math.max(index, 0), parlays.length - 1));
            }}
          >
            {parlays.map((parlay) => (
              <div key={parlay.parlay_id} className="parlay-slide">
                <ParlayTicketSlip ticket={parlay} />
              </div>
            ))}
          </div>
          {parlays.length > 1 && (
            <div className="carousel-legend" aria-live="polite">
              Parlay Slip {currentIndex + 1} of {parlays.length}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Parlays;